<script lang="ts">
	import Button from "./Button.svelte";
	import type { MenuItem } from "../../../core/schema/MenuItem.js";
	import { appName } from "../../../core/schema/names/appName.js";
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import Drawer from "./Drawer.svelte";
	import MobileMenu from "./Menu/MobileMenu.svelte";
	import DesktopMenu from "./Menu/DesktopMenu.svelte";
	import logo from "../../../core/assets/logo.svg";
	import logoBlue from "../../../core/assets/logo-blue.svg";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Header.translations.json";
	import { getContext } from "svelte";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getTenant } from "../getTenant.js";
	import LanguagePicker from "./LanguagePicker.svelte";
	import AlgoliaSearchbar from "./AlgoliaSearchbar.svelte";
	import { tenants } from "../../../core/config/tenants.js";
	import { homepagePathMap } from "../../../core/schema/paths/homepagePathMap.js";
	export let menu: MenuItem[];
	export let transparentMenu = false;

	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);

	const mobileMenuId = "mobile-menu";

	let isMenuOpen = false;
	let isSearchOpen = false;

	const { WEB_URL_OR_LOCAL } = getGlobalSettings();

	const tenant = getTenant();
	const translate = getTranslate(translations);

	function toggleMenu(): void {
		isMenuOpen = !isMenuOpen;
	}

	let scrollPosition = 0;

	function onScroll(event: UIEvent & { currentTarget: EventTarget & Window }): void {
		const { scrollY } = event.currentTarget;
		scrollPosition = Math.max(scrollY, 0);
	}
</script>

<svelte:window on:scroll={onScroll} />

{#if menu.length > 0}
	<Drawer
		id={mobileMenuId}
		label={translate("menu", {}, true)}
		bind:isOpen={isMenuOpen}
		position="left"
		dontShowCloseButton
	>
		<MobileMenu pageSlug={pageSlug[tenant.locale] ?? ""} bind:isOpen={isMenuOpen} {menu} logo={logoBlue} />
	</Drawer>
{/if}

<!-- TODO - grayscale added to prevent flickering of header on page scroll in FireFox - https://stackoverflow.com/questions/30411909/css-transition-flickering-in-firefox -->
<header
	class="header-with-backdrop border-b-1 hover:bg-ltprimary-150 fixed inset-x-0 top-0 z-30 mx-auto h-20 w-full border-b border-solid border-white border-opacity-50 py-3 grayscale-[1%] transition-[border,transform,background] duration-300 xl:h-auto xl:py-0"
	class:bg-ltprimary-150={!transparentMenu || scrollPosition > 0 || isSearchOpen}
	class:bg-transparent={transparentMenu && scrollPosition === 0 && !isSearchOpen}
	class:border-ltwhiteout={scrollPosition > 0}
	class:border-transparent={scrollPosition <= 0}
>
	<div class="mx-auto h-full max-w-[120rem]">
		<div
			class="flex w-full items-center justify-between px-2 md:px-0 xl:h-20 xl:grid-cols-[1fr,auto,1fr] xl:gap-2 xl:px-8 2xl:gap-12 2xl:pt-1"
		>
			<Button
				class="whitespace-nowrap rounded-3xl border-none transition-opacity duration-300 xl:hidden {isSearchOpen
					? 'pointer-events-none opacity-0'
					: ''}"
				variant="custom"
				on:click={toggleMenu}
				aria-label={translate("menu", {}, true)}
				aria-expanded={isMenuOpen}
				aria-controls={mobileMenuId}
			>
				<span class="text-xs font-semibold uppercase tracking-[0.15rem] text-white">{translate("menu")}</span>
			</Button>
			<a
				href="/{homepagePathMap[tenant.locale]}"
				class="h-auto w-32 transition-opacity duration-300 xl:w-40 {isSearchOpen
					? 'max-[1535px]:pointer-events-none max-[1535px]:opacity-0'
					: ''}"
			>
				<StaticPicture
					alt="Logo {appName}"
					image={logo}
					imgClass="w-full h-auto xl:w-full"
					loading="eager"
					width={181.61}
					height={20}
				/>
			</a>
			{#if menu.length > 0}
				<DesktopMenu
					class="flex-1 overflow-hidden whitespace-nowrap transition-opacity duration-300 {isSearchOpen
						? 'pointer-events-none opacity-0'
						: ''}"
					pageSlug={pageSlug[tenant.locale] ?? ""}
					{menu}
				/>
			{/if}
			<div class="flex items-center justify-end gap-4">
				<AlgoliaSearchbar bind:isOpen={isSearchOpen} />
				<div
					class="transition-opacity duration-300 {isSearchOpen ? 'max-sm:pointer-events-none max-sm:opacity-0' : ''}"
				>
					<LanguagePicker
						webUrl={WEB_URL_OR_LOCAL}
						currentLocale={tenant.locale}
						tenants={Object.values(tenants)}
						{pageSlug}
					/>
				</div>
			</div>
		</div>
	</div>
</header>

<div
	aria-hidden="true"
	class="header-backdrop pointer-events-none fixed inset-x-0 bottom-0 top-20 z-[25] hidden bg-black/40 opacity-0 transition-opacity duration-300 xl:block"
></div>
